import type { LoginOptions, LogoutOptions } from "@jetblue/kit-auth";
import { useAuth, useValidAccessToken } from "@jetblue/kit-auth-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const toError = (message: string, cause: unknown): Error =>
  new Error(message, { cause });

type UseKitAuthProperties = {
  isAuthenticated: boolean;
  accessToken: string | undefined;
  error: Error | null;
  isLoading: boolean;
  handleLogin: (options?: LoginOptions) => Promise<void>;
  handleLogout: (options?: LogoutOptions) => Promise<void>;
};

export const useKitAuth = (): UseKitAuthProperties => {
  const { login, logout, hasIdpSession, state } = useAuth();
  const getValidAccessToken = useValidAccessToken();

  const authQuery = useQuery({
    gcTime: 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let hasSession: boolean | null = null;
      let failure: Error | null = null;

      try {
        hasSession = await hasIdpSession();
      } catch (error) {
        failure = toError("kit-auth could not verify the IdP session", error);
      }

      if (hasSession === false) {
        return { failure, hasSession, token: null };
      }

      try {
        const token = await getValidAccessToken();
        return { failure, hasSession, token: token || null };
      } catch (error) {
        return {
          failure:
            failure ??
            toError("kit-auth could not produce an access token", error),
          hasSession,
          token: null,
        };
      }
    },
    queryKey: ["kit-auth-session"],
    staleTime: 0,
  });
  return {
    accessToken: authQuery.data?.token ?? undefined,
    error: authQuery.data?.failure ?? null,
    handleLogin: login,
    handleLogout: logout,
    isAuthenticated: authQuery.data?.hasSession !== false && state !== "idle",
    isLoading: authQuery.isLoading,
  };
};
